import React, { useState } from "react";
import { Link } from "react-router-dom";
import { User, Briefcase, ClipboardCheck, Phone, Mail, MapPin, Wrench } from "lucide-react";
import { Pill } from "@/components/StatusPill";
import { SourceBadge } from "@/components/EnquirySource";
import { InlineEdit } from "@/components/InlineEdit";
import { DEPT_COLOUR } from "@/lib/departments";

const MASK = "••••••••";
const PAY_TONE = {
  "Fully Paid": "bg-emerald-50 text-emerald-700 border-emerald-200",
  _pill: {
    green: "bg-green-50 text-green-700 border-green-200", blue: "bg-blue-50 text-blue-700 border-blue-200", indigo: "bg-indigo-50 text-indigo-700 border-indigo-200", purple: "bg-purple-50 text-purple-700 border-purple-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200", teal: "bg-teal-50 text-teal-700 border-teal-200", amber: "bg-amber-50 text-amber-700 border-amber-200", pink: "bg-pink-50 text-pink-700 border-pink-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200", rose: "bg-rose-50 text-rose-700 border-rose-200", emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", slate: "bg-slate-50 text-slate-700 border-slate-200",
  },
  "Deposit Paid": "bg-sky-50 text-sky-700 border-sky-200",
  "Awaiting Deposit": "bg-amber-50 text-amber-700 border-amber-200",
};

function V({ children, testid }) {
  if (children === MASK) return <span className="text-slate-400 tracking-widest" data-testid={testid}>{MASK}</span>;
  return children ? <span className="text-slate-800 break-words" data-testid={testid}>{children}</span> : <span className="text-slate-300" data-testid={testid}>—</span>;
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 items-baseline text-[13px] leading-5">
      <span className="text-slate-500">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function ContactLink({ href, value, Icon, testid }) {
  if (!value || value === MASK) return <span className="inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-slate-300" /><V testid={testid}>{value}</V></span>;
  return (
    <a href={href} className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 hover:underline break-all" data-testid={testid}>
      <Icon className="w-3.5 h-3.5 shrink-0" />{value}
    </a>
  );
}

function Col({ Icon, title, children, testid }) {
  return (
    <div className="min-w-0 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <Icon className="w-3.5 h-3.5" />{title}
      </div>
      {children}
    </div>
  );
}

function Instruction({ label, text, testid, field, edit }) {
  const [open, setOpen] = useState(false);
  const long = !!text && text !== MASK && (text.length > 180 || text.split("\n").length > 3);
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <InlineEdit value={text === MASK ? "" : text} kind="textarea" canEdit={edit.canEdit && text !== MASK} testid={testid} onSave={(v) => edit.onSave({ [field]: v }, `${label} updated`)}>
        <div className={`text-[13px] leading-5 whitespace-pre-wrap break-words ${long && !open ? "line-clamp-3" : ""}`}><V>{text}</V></div>
      </InlineEdit>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-700" data-testid={`${testid}-toggle`}>
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
const parts = (s) => (s && s !== MASK ? String(s).split(/,|\n/).map((p) => p.trim()).filter(Boolean) : []);

export function fullAddress(t) {
  if ([t.venue_name, t.venue_address].includes(MASK)) return MASK;
  const sa = t.site_address || {};
  const street = [...parts(sa.line1), ...parts(sa.line2)];
  const name = parts(t.venue_name), addr = [...parts(t.venue_address), ...parts(t.full_address)];
  const base = addr.length ? addr : street, extra = addr.length ? street : [];
  const addrKeys = new Set([...base, ...extra].map(norm));
  const ordered = name.every((p) => addrKeys.has(norm(p))) ? [...base, ...extra] : [...name, ...base, ...extra];
  const pc = t.venue_postcode && t.venue_postcode !== MASK ? t.venue_postcode.trim() : "";
  const seen = new Set(), out = [];
  [...ordered, ...parts(t.venue_city), ...parts(sa.city)].forEach((p) => {
    const k = norm(p);
    if (k && !seen.has(k) && k !== norm(pc)) { seen.add(k); out.push(p); }
  });
  if (pc) out.push(pc);
  return out.join(", ");
}

// Venue = job site address; when it is only part of the customer's address (e.g. town + postcode), use the fuller customer address.
export function venueAddress(t, endUser) {
  const v = fullAddress(t);
  const c = endUser?.address && endUser.address !== MASK ? endUser.address : "";
  if (!c || v === MASK) return v || c;
  const ck = new Set(parts(c).map(norm));
  return !v || parts(v).every((p) => ck.has(norm(p))) ? c : v;
}

function MapLink({ address, testid }) {
  if (!address || address === MASK) return <V testid={testid}>{address}</V>;
  return (
    <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 hover:underline break-words" title="Open directions in Google Maps" data-testid={testid}>{address}</a>
  );
}

function CustomerCol({ t, endUser }) {
  const phone = endUser?.telephone || endUser?.mobile;
  return (
    <Col Icon={User} title="Customer" testid="overview-customer">
      <div className="text-[15px] font-semibold text-slate-900 break-words">
        {endUser ? (
          <Link to={`/users?q=${encodeURIComponent(endUser.full_name || "")}`} className="text-indigo-600 hover:underline" data-testid="task-enduser-link">{endUser.full_name}</Link>
        ) : <V testid="overview-public-contact">{t.public_contact}</V>}
      </div>
      <div className="space-y-1 text-[13px]">
        <div><ContactLink href={`tel:${(phone || "").replace(/\s+/g, "")}`} value={phone} Icon={Phone} testid="overview-phone" /></div>
        <div><ContactLink href={`mailto:${endUser?.email || ""}`} value={endUser?.email} Icon={Mail} testid="overview-email" /></div>
      </div>
      <div className="pt-2 space-y-1" data-testid="overview-venue">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Venue</div>
        <div className="flex items-start gap-1.5 text-[13px] leading-5">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
          <MapLink address={venueAddress(t, endUser)} testid="overview-address" />
        </div>
      </div>
    </Col>
  );
}

function DetailsCol({ t }) {
  return (
    <Col Icon={Briefcase} title="Job Details" testid="overview-details">
      <div className="text-[15px] font-semibold text-slate-900 break-words" data-testid="overview-title">{t.title || <span className="text-slate-300">—</span>}</div>
      <Row label="Job type">{t.task_type ? <Pill colour={t.task_type_colour} data-testid="overview-job-type">{t.task_type}</Pill> : <V />}</Row>
      <Row label="Work type"><V testid="overview-work-type">{t.work_type}</V></Row>
      <Row label="Source">{t.enquiry_source ? <SourceBadge name={t.enquiry_source} testid="task-enquiry-source" /> : <V />}</Row>
      <Row label="Sales contact"><V testid="overview-sales-contact">{t.sales_contact}</V></Row>
    </Col>
  );
}

const ENGINEER = {
  BOOKED: ["Booked", "bg-slate-50 text-slate-600 border-slate-200"],
  IN_PROGRESS: ["Engineer: in progress", "bg-sky-50 text-sky-700 border-sky-200"],
  COMPLETE: ["Engineer: work complete", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  REBOOK: ["Rebook needed", "bg-amber-50 text-amber-700 border-amber-200"],
};

function EngineerChip({ value }) {
  const c = ENGINEER[(value || "").trim().toUpperCase()];
  if (!c) return null;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${c[1]}`} data-testid="overview-engineer-progress"><Wrench className="w-3 h-3" />{c[0]}</span>;
}

function StatusCol({ t }) {
  const pay = t.final_payment_date ? "Fully Paid" : t.deposit_date ? "Deposit Paid" : "Awaiting Deposit";
  return (
    <Col Icon={ClipboardCheck} title="Status" testid="overview-status">
      <Row label="Department">{t.current_department ? <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${PAY_TONE._pill[DEPT_COLOUR[t.current_department]] || PAY_TONE._pill.slate}`} data-testid="overview-dept-badge">{t.current_department}</span> : <V />}</Row>
      <Row label="Payment"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${PAY_TONE[pay]}`} data-testid="overview-payment-status">{pay}</span></Row>
      <Row label="Deposit date"><V testid="overview-deposit-date">{t.deposit_date}</V></Row>
      <EngineerChip value={t.job_summary_status} />
    </Col>
  );
}

export function JobOverview({ t, endUser, canEdit = false, onSave }) {
  const edit = { canEdit, onSave };
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid="job-overview">
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
        <h2 className="text-base font-semibold text-slate-900 tracking-tight">Job Overview</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-5 px-5 pb-4">
        <CustomerCol t={t} endUser={endUser} />
        <DetailsCol t={t} />
        <StatusCol t={t} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 px-5 py-3.5 border-t border-slate-100">
        <Instruction label="Work Instructions" field="work_instructions" text={t.work_instructions} testid="overview-work-instructions" edit={edit} />
        <Instruction label="Client Instructions" field="client_instructions" text={t.client_instructions} testid="overview-client-instructions" edit={edit} />
      </div>
    </section>
  );
}
